// src/components/SocialMediaPackages.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { motion } from 'framer-motion';

/* ────────────────────────── 1. Your WhatsApp number ───────────────────────── */
const WHATSAPP_NUMBER = '94770837053'; // international format, no '+' or dashes

/* ────────────────────────── 2. Plan Data ──────────────────────────────── */
const plans = [
  {
    key: 'starter',
    title: 'Starter Package',
    subtitle: 'For small businesses or personal brands starting out',
    features: [
      { label: '8 custom posts/month', included: true },
      { label: 'All social media platforms', included: true },
      { label: 'Hashtag strategy', included: true },
      { label: 'Performance report', included: true },
      { label: 'Respond to customers', included: false },
    ],
    price: 'LKR 10,000',
    recommended: false,
  },
  {
    key: 'growth',
    title: 'Growth Package',
    subtitle: 'Ideal for growing brands that want more visibility',
    features: [
      { label: 'Respond to customers', included: true },
      { label: '16 custom posts/month', included: true },
      { label: 'All social media platforms', included: true },
      { label: 'Graphics & captions', included: true },
      { label: 'Hashtag & timing strategy', included: true },
      { label: '2 reels/video edits', included: true },
      { label: 'Performance report', included: true },
    ],
    price: 'LKR 25,000',
    recommended: true,
  },
  {
    key: 'pro',
    title: 'Pro Package',
    subtitle: 'Complete management & creative dominance',
    features: [
      { label: 'Everything in Growth Package', included: true },
      { label: 'Unlimited posts/month', included: true },
      { label: '8 high-quality videos/reels', included: true },
      { label: 'Website + Android App', included: true },
      { label: '24/7 Customer Service', included: true },
    ],
    price: 'LKR 60,000',
    recommended: false,
  },
];

/* ────────────────────────── 3. Helper to send WhatsApp ───────────────────────── */
function sendToWhatsApp(plan) {
  const { title, price, features } = plan;
  const featuresList = features
    .map(f => `- ${f.label}${f.included ? '' : ' (not included)'}`)
    .join('\n');
  const message = encodeURIComponent(
    `Hi Lemda Team,\n\n` +
    `I’m interested in your *${title}* priced at *${price}*.\n` +
    `Features:\n${featuresList}\n\n` +
    `Please let me know the next steps.`
  );
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
}

/* ────────────────────────── 4. Component ──────────────────────────────── */
export default function SocialMediaPackages() {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
        color: 'common.white',
      }}
    >
      <Container>
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          gutterBottom
          sx={{ color: 'common.white' }}
        >
          Social Media Marketing
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ mb: 6, opacity: 0.9, color: 'common.white' }}
        >
          Choose a plan that’s right for you.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: { xs: 'flex-start', md: 'center' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            px: { xs: 2, md: 0 },
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {plans.map(plan => (
            <motion.div
              key={plan.key}
              whileHover={{ y: -8 }}
              transition={{ type: 'spring', stiffness: 300 }}
              style={{
                flex: '0 0 300px',
                minWidth: 300,
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: 4,
                  border: plan.recommended
                    ? `2px solid ${theme.palette.secondary.main}`
                    : '2px solid transparent',
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: -32,
                      transform: 'rotate(45deg)',
                      width: 120,
                      textAlign: 'center',
                      bgcolor: theme.palette.secondary.main,
                      color: 'common.white',
                      fontWeight: 700,
                      py: 0.5,
                    }}
                  >
                    Popular
                  </Box>
                )}

                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {plan.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                    {plan.subtitle}
                  </Typography>

                  <Stack spacing={1} alignItems="flex-start">
                    {plan.features.map((f, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                        {f.included ? (
                          <CheckCircle
                            fontSize="small"
                            sx={{ color: theme.palette.success.main, mr: 1 }}
                          />
                        ) : (
                          <Cancel
                            fontSize="small"
                            sx={{ color: theme.palette.error.main, mr: 1 }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{ color: f.included ? 'text.primary' : 'text.disabled' }}
                        >
                          {f.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>

                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: plan.recommended
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ color: 'common.white' }}>
                    {plan.price}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'common.white', mb: 2, display: 'block' }}
                  >
                    /month
                  </Typography>
                  <Button
                    variant="contained"
                    color={plan.recommended ? 'secondary' : 'primary'}
                    sx={{ textTransform: 'none' }}
                    onClick={() => sendToWhatsApp(plan)}
                  >
                    Get Started
                  </Button>
                </Box>
              </Card>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
